import { formatDate } from '@/utils/formatters';
import type {
  CarryFundSummary,
  CarryHolder,
  CarryUnitPosition,
  CarryVestingPeriod,
} from '@/types/carryFund';
import type { FundMemberOption } from '@/types/fundMember';

export type UnitCounts = Pick<CarryUnitPosition, 'units' | 'vested_units'>;

export function isSetupComplete(fund: CarryFundSummary): boolean {
  return Boolean(fund.vesting_start_date && fund.vesting_end_date);
}

export function poolShare(units: number, totalUnits: number): number {
  return totalUnits ? units / totalUnits : 0;
}

export function unvestedUnits({ units, vested_units }: UnitCounts): number {
  return units - vested_units;
}

export function vestedShare({ units, vested_units }: UnitCounts): number {
  return units ? vested_units / units : 0;
}

/** Units and vested units summed, over a fund's holders or a member's holdings. */
export function unitTotals(rows: UnitCounts[]): UnitCounts {
  return {
    units: rows.reduce((sum, r) => sum + r.units, 0),
    vested_units: rows.reduce((sum, r) => sum + r.vested_units, 0),
  };
}

export function formatUnits(units: number): string {
  return units.toLocaleString('en-US');
}

export function formatDays(days: number): string {
  return days.toLocaleString('en-US');
}

export function formatShare(fraction: number, decimals = 0): string {
  return `${(fraction * 100).toFixed(decimals)}%`;
}

export function vestingPeriod({
  vesting_start_date,
  vesting_end_date,
}: Pick<
  CarryVestingPeriod,
  'vesting_start_date' | 'vesting_end_date'
>): string {
  return `${formatDate(vesting_start_date)} – ${formatDate(vesting_end_date)}`;
}

/** An amount in thousands, e.g. 16720000 → "16,720". */
export function formatThousands(amount: string | number): string {
  return Math.round(Number(amount) / 1000).toLocaleString('en-US');
}

export function currencySymbol(currency: string): string {
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? currency;
}

export type VestingState = 'VESTING' | 'FROZEN' | 'FULLY_VESTED' | 'NO_PERIOD';

export const VESTING_BAR_CLASSES: Record<VestingState, string> = {
  VESTING: '[&>div]:bg-slate-700 dark:[&>div]:bg-slate-300',
  FROZEN: '[&>div]:bg-slate-400',
  FULLY_VESTED: '[&>div]:bg-emerald-500',
  NO_PERIOD: '',
};

/** Where a vesting period stands on `asOf`, leaving frozen holders aside. */
export function periodState(
  start: string | null,
  end: string | null,
  asOf: string
): VestingState {
  if (!start || !end) return 'NO_PERIOD';
  return asOf >= end ? 'FULLY_VESTED' : 'VESTING';
}

type VestingPosition = CarryVestingPeriod & Partial<CarryUnitPosition>;

export function vestingState(position: VestingPosition): VestingState {
  if (position.vesting_status === 'FROZEN') return 'FROZEN';
  return periodState(
    position.vesting_start_date,
    position.vesting_end_date,
    position.as_of
  );
}

export function vestingNote(position: VestingPosition): string {
  const state = vestingState(position);
  if (state === 'FROZEN') {
    return `Frozen on ${formatDate(position.frozen_on)} · ${formatUnits(position.vested_units ?? 0)} units vested`;
  }
  if (state === 'FULLY_VESTED') return 'Vesting period ended';
  return vestingDaysNote(position.vested_days, position.unvested_days);
}

export function vestingDaysNote(
  vested: number | null,
  left: number | null
): string {
  if (vested === null || left === null) return '';
  return `${formatDays(vested)} days vested · ${formatDays(left)} left`;
}

/** A multiple such as MOM, e.g. "1.30" → "1.30×". */
export function formatMultiple(multiple: string): string {
  return `${Number(multiple).toFixed(2)}×`;
}

export function undoFreezeText(holder: CarryHolder): string {
  const back =
    holder.returned_units === null
      ? ''
      : ` Their ${formatUnits(holder.returned_units)} returned units come back, and they`;
  const subject = back || ' They';
  return `Undo the freeze of ${holder.full_name}?${subject} vest on the fund's schedule again as if never frozen. The cessation of vesting agreement is withdrawn.`;
}

export function unallocatedUnits(fund: CarryFundSummary): number {
  return Math.max(fund.total_carry_units - fund.allocated_units, 0);
}

// Frozen holders get no more units until the freeze is undone.
export function receivingMembers(
  members: FundMemberOption[],
  holders: CarryHolder[],
  excludedId?: string
): FundMemberOption[] {
  const frozen = new Set(
    holders
      .filter((h) => h.vesting_status === 'FROZEN')
      .map((h) => h.fund_member_id)
  );
  return members.filter((m) => !frozen.has(m.id) && m.id !== excludedId);
}

export const NUM_CELL = 'text-right';
export const MUTED_NUM_CELL = 'text-right text-muted-foreground';

type FundFacts = Pick<
  CarryFundSummary,
  'display_name' | 'coc_number' | 'carried_interest'
>;

export function carryFundFacts(
  fund: FundFacts,
  ...extra: (string | null | false)[]
): string {
  return [
    fund.display_name,
    fund.coc_number && `CoC ${fund.coc_number}`,
    `Carry ${formatShare(Number(fund.carried_interest))}`,
    ...extra,
  ]
    .filter(Boolean)
    .join(' · ');
}
